"use client";

import { Mic, Camera, MonitorSpeaker, Video, SwitchCamera, Volume2 } from "lucide-react";
import { Switch } from "@speakai/ui/switch";
import { Combobox } from "@/components/shared/combobox";
import { useTranslation } from "react-i18next";

const QUALITY_OPTIONS = [
  { value: "480p", label: "480p, Standard" },
  { value: "720p", label: "720p, HD" },
  { value: "1080p", label: "1080p, Full HD" },
];

// min-h-11 = 44px minimum tap target; truncate prevents long device labels from overflowing.
const selectClassName = "w-full min-h-11 truncate";

interface DeviceSelectorProps {
  mode: "audio" | "video" | "screen";
  microphones: { id: string; label: string }[];
  cameras: { id: string; label: string }[];
  selectedMic: string;
  onMicChange: (value: string) => void;
  selectedCamera: string;
  onCameraChange: (value: string) => void;
  selectedQuality: string;
  onQualityChange: (value: string) => void;
  withCameraOverlay?: boolean;
  onWithCameraOverlayChange?: (value: boolean) => void;
  audioOutputDevices?: { id: string; label: string }[];
  selectedOutputDevice?: string;
  onOutputDeviceChange?: (id: string) => void;
  onSwitchCamera?: () => void;
}

// Covers iOS Safari, Android Chrome, iPadOS (desktop UA but touch-capable), and DevTools mobile emulation.
function isTouchDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  if (navigator.maxTouchPoints > 0) return true;
  return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function DeviceSelector({
  mode,
  microphones,
  cameras,
  selectedMic,
  onMicChange,
  selectedCamera,
  onCameraChange,
  selectedQuality,
  onQualityChange,
  withCameraOverlay = false,
  onWithCameraOverlayChange,
  audioOutputDevices = [],
  selectedOutputDevice = "",
  onOutputDeviceChange,
  onSwitchCamera,
}: DeviceSelectorProps) {
  const { t } = useTranslation();
  const showSwitchCamera = isTouchDevice();
  return (
    <div className="grid w-full grid-cols-1 gap-3">
      {/* Microphone */}
      {microphones.length > 0 && (
        <div className="flex flex-col gap-2">
          <label htmlFor="recorder-mic-select" className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Mic className="h-3.5 w-3.5" aria-hidden="true" />
            {t("RECORD.MICROPHONE", "Microphone")}
          </label>
          <Combobox
            id="recorder-mic-select"
            value={selectedMic}
            onChange={onMicChange}
            options={microphones.map((mic) => ({ value: mic.id, label: mic.label }))}
            aria-label={t("RECORD.MICROPHONE", "Microphone")}
            data-testid="recorder-mic-select"
            className={selectClassName}
            hideSearch
          />
        </div>
      )}

      {mode === "screen" && (
        <div className="flex flex-col gap-2">
          <label
            htmlFor="screen-camera-overlay-toggle"
            className="flex items-center gap-2 text-xs font-medium text-muted-foreground"
          >
            <Video className="h-3.5 w-3.5" aria-hidden="true" />
            {t("RECORD.SCREEN_CAMERA_OVERLAY", "Camera overlay")}
          </label>
          <div className="flex min-h-11 items-center justify-between rounded-lg border border-border bg-card px-3 py-2">
            <span className="text-sm text-foreground">
              {withCameraOverlay
                ? t("RECORD.SCREEN_CAMERA_OVERLAY_ON", "Camera-in-screen overlay enabled")
                : t("RECORD.SCREEN_CAMERA_OVERLAY_OFF", "Screen recording only")}
            </span>
            <Switch
              id="screen-camera-overlay-toggle"
              checked={withCameraOverlay}
              onChange={(value) => onWithCameraOverlayChange?.(value)}
              aria-label={t(
                "RECORD.SCREEN_CAMERA_OVERLAY_LABEL",
                "Toggle camera overlay on screen recording",
              )}
              data-testid="recorder-camera-overlay-toggle"
            />
          </div>
        </div>
      )}

      {mode === "video" && (
        <>
          <div className="flex flex-col gap-2">
            <label htmlFor="recorder-camera-select" className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Camera className="h-3.5 w-3.5" aria-hidden="true" />
              {t("RECORD.CAMERA", "Camera")}
            </label>
            {cameras.length > 0 ? (
              <Combobox
                id="recorder-camera-select"
                value={selectedCamera}
                onChange={onCameraChange}
                options={cameras.map((cam) => ({ value: cam.id, label: cam.label }))}
                aria-label={t("RECORD.CAMERA", "Camera")}
                data-testid="recorder-camera-select"
                className={selectClassName}
                hideSearch
              />
            ) : null}

            {showSwitchCamera && onSwitchCamera && cameras.length > 1 && (
              <button
                type="button"
                onClick={onSwitchCamera}
                aria-label={t("RECORD.SWITCH_CAMERA", "Switch camera")}
                data-testid="recorder-switch-camera"
                className="mt-1 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <SwitchCamera className="h-4 w-4" aria-hidden="true" />
                {t("RECORD.SWITCH_CAMERA", "Switch camera")}
              </button>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="recorder-quality-select" className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <MonitorSpeaker className="h-3.5 w-3.5" aria-hidden="true" />
              {t("RECORD.VIDEO_QUALITY", "Video quality")}
            </label>
            <Combobox
              id="recorder-quality-select"
              value={selectedQuality}
              onChange={onQualityChange}
              options={QUALITY_OPTIONS}
              aria-label={t("RECORD.VIDEO_QUALITY", "Video quality")}
              data-testid="recorder-quality-select"
              className={selectClassName}
              hideSearch
            />
          </div>
        </>
      )}

      {audioOutputDevices.length > 0 && onOutputDeviceChange && (
        <div className="flex flex-col gap-2">
          <label htmlFor="recorder-output-select" className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Volume2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t("RECORD.SPEAKER_OUTPUT", "Speaker output")}
          </label>
          <Combobox
            id="recorder-output-select"
            value={selectedOutputDevice}
            onChange={onOutputDeviceChange}
            options={audioOutputDevices.map((d) => ({ value: d.id, label: d.label }))}
            aria-label={t("RECORD.SPEAKER_OUTPUT", "Speaker output")}
            data-testid="recorder-output-select"
            className={selectClassName}
            hideSearch
          />
        </div>
      )}
    </div>
  );
}
